import { supabase } from "@/lib/supabase";

// ----- Follows -----
export async function getFollowCounts(userId) {
  const [{ count: followers = 0 }, { count: following = 0 }] = await Promise.all([
    supabase.from("follows").select("*", { count: "exact", head: true }).eq("following_id", userId),
    supabase.from("follows").select("*", { count: "exact", head: true }).eq("follower_id", userId),
  ]);
  return { followers, following };
}

export async function isFollowing(viewerId, targetId) {
  if (!viewerId || viewerId === targetId) return false;
  const { data } = await supabase
    .from("follows")
    .select("follower_id")
    .eq("follower_id", viewerId)
    .eq("following_id", targetId)
    .maybeSingle();
  return !!data;
}

export async function follow(viewerId, targetId) {
  const { error } = await supabase
    .from("follows")
    .insert({ follower_id: viewerId, following_id: targetId });
  if (error && error.code !== "23505") throw error;
}

export async function unfollow(viewerId, targetId) {
  const { error } = await supabase
    .from("follows")
    .delete()
    .eq("follower_id", viewerId)
    .eq("following_id", targetId);
  if (error) throw error;
}

export async function listFollowingIds(userId) {
  const { data, error } = await supabase
    .from("follows")
    .select("following_id")
    .eq("follower_id", userId);
  if (error) throw error;
  return (data || []).map((r) => r.following_id);
}

/**
 * searchPeople — autocomplete-style profile search used by the feed.
 *
 * Matches:
 *   • username (handle), case-insensitive prefix-and-contains
 *   • display_name, contains
 *   • full_name,    contains
 *
 * Soft-deleted and suspended profiles are filtered out so the feed never
 * surfaces ghosted accounts. Results are capped at `limit` (default 8) so
 * the inline suggestions list stays scannable.
 *
 * Why prefix-style matching: Supabase's PostgREST `or()` already supports
 * `ilike.*term*`, which is fine on a small profiles table. If the table
 * scales past ~50k profiles, we'd swap this for `tsvector` + an FTS index
 * — but for now this is fast enough and avoids a schema migration.
 */
export async function searchPeople(query, { limit = 8, signal } = {}) {
  const q = String(query || "").trim();
  if (q.length < 2) return [];
  const like = `%${q.replace(/[%_]/g, "")}%`;
  let req = supabase
    .from("profiles")
    .select("id, display_name, username, full_name, avatar_url, city, is_verified_admin")
    .or([
      `username.ilike.${like}`,
      `display_name.ilike.${like}`,
      `full_name.ilike.${like}`,
    ].join(","))
    .eq("is_deleted", false)
    .eq("is_suspended", false)
    .limit(limit);
  if (signal) req = req.abortSignal(signal);
  const { data, error } = await req;
  if (error) throw error;
  return data || [];
}

// ----- Public profile -----
const PUBLIC_PROFILE_FIELDS =
  "id, display_name, username, full_name, avatar_url, bio, city, " +
  "whatsapp, instagram, snapchat, is_verified_admin, is_deleted, is_suspended";

// Lookup by UUID first, fall back to username (case-insensitive).
export async function getPublicProfile(handle) {
  if (!handle) return null;
  const looksLikeId =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(handle);
  let data = null;
  if (looksLikeId) {
    const { data: byId, error } = await supabase
      .from("profiles")
      .select(PUBLIC_PROFILE_FIELDS)
      .eq("id", handle)
      .maybeSingle();
    if (error) throw error;
    data = byId;
  }
  if (!data) {
    const { data: byHandle, error } = await supabase
      .from("profiles")
      .select(PUBLIC_PROFILE_FIELDS)
      .ilike("username", String(handle).toLowerCase())
      .maybeSingle();
    if (error) throw error;
    data = byHandle;
  }
  // Hide soft-deleted profiles from the public app.
  if (!data || data.is_deleted) return null;
  return data;
}

// ----- Notifications -----
async function attachActorProfiles(rows) {
  const ids = Array.from(new Set(rows.map((r) => r.actor_id).filter(Boolean)));
  if (ids.length === 0) {
    return rows.map((r) => ({ ...r, actor: null }));
  }
  const { data: profs } = await supabase
    .from("profiles")
    .select("id, display_name, username, avatar_url")
    .in("id", ids);
  const map = new Map((profs || []).map((p) => [p.id, p]));
  return rows.map((r) => ({ ...r, actor: r.actor_id ? map.get(r.actor_id) || null : null }));
}

export async function listNotifications(userId, { limit = 50 } = {}) {
  const { data, error } = await supabase
    .from("notifications")
    .select("id, type, payload, read, created_at, event_id, actor_id")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return attachActorProfiles(data || []);
}

export async function unreadNotificationCount(userId) {
  const { count, error } = await supabase
    .from("notifications")
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("read", false);
  if (error) throw error;
  return count || 0;
}

export async function markNotificationRead(id) {
  const { error } = await supabase.from("notifications").update({ read: true }).eq("id", id);
  if (error) throw error;
}

export async function markAllNotificationsRead(userId) {
  const { error } = await supabase
    .from("notifications")
    .update({ read: true })
    .eq("user_id", userId)
    .eq("read", false);
  if (error) throw error;
}

// ----- Messages / DMs -----
export async function listConversations(userId) {
  const { data, error } = await supabase
    .from("messages")
    .select("id, sender_id, recipient_id, content, read, created_at")
    .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;

  const counterpartIds = Array.from(
    new Set((data || []).map((m) => (m.sender_id === userId ? m.recipient_id : m.sender_id)))
  );
  const { data: profs } = counterpartIds.length
    ? await supabase.from("profiles").select("id, display_name, username, avatar_url").in("id", counterpartIds)
    : { data: [] };
  const profMap = new Map((profs || []).map((p) => [p.id, p]));

  const threads = new Map();
  for (const m of data || []) {
    const counterpartId = m.sender_id === userId ? m.recipient_id : m.sender_id;
    if (threads.has(counterpartId)) continue;
    const cp = profMap.get(counterpartId);
    threads.set(counterpartId, {
      counterpart_id: counterpartId,
      counterpart_name: cp?.display_name || "anon",
      counterpart_username: cp?.username || null,
      counterpart_avatar_url: cp?.avatar_url || null,
      last_message: m.content,
      last_at: m.created_at,
      unread: !m.read && m.recipient_id === userId,
    });
  }
  return Array.from(threads.values());
}

export async function listThread(userId, otherId, { limit = 200 } = {}) {
  const { data, error } = await supabase
    .from("messages")
    .select("id, sender_id, recipient_id, content, read, created_at")
    .or(
      `and(sender_id.eq.${userId},recipient_id.eq.${otherId}),and(sender_id.eq.${otherId},recipient_id.eq.${userId})`
    )
    .order("created_at", { ascending: true })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function sendMessage(senderId, recipientId, content) {
  const trimmed = content.trim();
  if (!trimmed) return null;
  const { data, error } = await supabase
    .from("messages")
    .insert({ sender_id: senderId, recipient_id: recipientId, content: trimmed })
    .select("id, sender_id, recipient_id, content, read, created_at")
    .single();
  if (error) throw error;
  return data;
}

export async function markThreadRead(userId, otherId) {
  const { error } = await supabase
    .from("messages")
    .update({ read: true })
    .eq("recipient_id", userId)
    .eq("sender_id", otherId)
    .eq("read", false);
  if (error) throw error;
}

export async function unreadMessagesCount(userId) {
  const { count, error } = await supabase
    .from("messages")
    .select("id", { count: "exact", head: true })
    .eq("recipient_id", userId)
    .eq("read", false);
  if (error) throw error;
  return count || 0;
}
